import { FeatureFlag } from 'featureFlags';
import Immutable from 'immutable';
import { getAppServices } from 'providers/app-services';
import {
  ConnectedAccount,
  ConnectedAccountList,
  RawConnectedAccount,
} from 'records/ConnectedAccount';
import {
  IntegrationsDrive,
  RawIntegrationsDrive,
} from 'records/IntegrationsDrive';
import {
  IntegrationsFolder,
  RawIntegrationsFolder,
} from 'records/IntegrationsFolder';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import {
  IntegrationsSite,
  RawIntegrationsSite,
} from 'records/IntegrationsSite';
import GQLRequest from 'utils/GQLRequest';

export const getConnectedAccounts = async (
  selectedProject: string | null
): Promise<ConnectedAccountList> => {
  const { myConnectedAccounts } = await new GQLRequest()
    .query(
      `
      query($projectId: String){
        myConnectedAccounts(projectid: $projectId){
        connectedAccounts{
          id
          label
          ownerId
          providerType
          createdAt
          updatedAt
          expiresAt
          isExpired
        }
        id
      }
    }
      `
    )
    .variables({ projectId: selectedProject })
    .json<{
      myConnectedAccounts: { connectedAccounts: RawConnectedAccount[] };
    }>();

  return Immutable.List(
    myConnectedAccounts.connectedAccounts.map(ConnectedAccount.deserialize)
  );
};

export const getIntegrationSites = async (
  connectionId: string
): Promise<Immutable.List<IntegrationsSite>> => {
  const request = {
    connectionid: connectionId,
  };

  const { dmsProviderSites } = await new GQLRequest()
    .query(
      `
      query($request: DmsProviderSitesRequest!){
        dmsProviderSites(request: $request){
          id
          label
        }
      }
      `
    )
    .variables({ request })
    .json<{
      dmsProviderSites: RawIntegrationsSite[];
    }>();

  return Immutable.List(dmsProviderSites.map(IntegrationsSite.deserialize));
};

export const getIntegrationDrives = async (
  integrationAccountId: string,
  providerType: string,
  connectionId: string,
  siteId?: string
): Promise<Immutable.List<IntegrationsDrive>> => {
  const request = {
    projectid: integrationAccountId, // TODO: in the future, projectid should be renamed in the stack to integrationId
    providertype: providerType,
    connectionid: connectionId,
    siteid: siteId,
  };

  const { dmsProviderDrives } = await new GQLRequest()
    .query(
      `
      query($request: DmsProviderDrivesRequest!){
        dmsProviderDrives(request: $request){
          id
          lastModified
          name
          type
        }
      }
      `
    )
    .variables({ request })
    .json<{
      dmsProviderDrives: RawIntegrationsDrive[];
    }>();

  return Immutable.List(dmsProviderDrives.map(IntegrationsDrive.deserialize));
};

export const getIntegrationFolder = async (
  connectionId: string,
  projectId: string,
  providerType: string,
  driveId: string,
  folderId?: string
): Promise<IntegrationsFolder> => {
  const request = {
    connectionid: connectionId,
    projectid: projectId,
    providertype: providerType,
    driveid: driveId,
    folderid: folderId,
  };

  const { dmsFolder } = await new GQLRequest()
    .query(
      `
      query($request: DmsFolderRequest){
        dmsFolder(request: $request){
          items {
            documents {
              id
              key
              name
              updatedAt
            }
            folders {
              id
              key
              name
              updatedAt
            }
          }
          metadata {
            id
            key
            name
            type
          }
        }
      }
      `
    )
    .variables({ request })
    .json<{
      dmsFolder: RawIntegrationsFolder;
    }>();

  return IntegrationsFolder.deserialize(dmsFolder);
};

type ImportFile = {
  driveID: string;
  id: string;
  name: string;
  key: string;
};

export const importDrawingsFromDms = async (
  projectId: string,
  providerType: string,
  connectionId: string,
  selectedItems: Immutable.List<IntegrationsSelectedItem>
): Promise<string> => {
  const gqlProviderType = (() => {
    return providerType.toUpperCase();
  })();

  let folderDriveId: string | null = null;

  const folders = selectedItems.reduce((foldersToImport, selectedItem) => {
    const serializedItem = selectedItem.serialize();
    if (serializedItem.itemType === 'folder' && serializedItem.item) {
      folderDriveId = serializedItem.driveId;
      foldersToImport.push(serializedItem.item.id);
    }
    return foldersToImport;
  }, [] as string[]);

  const imports = selectedItems.reduce((filesToImport, selectedItem) => {
    const serializedItem = selectedItem.serialize();

    if (serializedItem.itemType === 'file' && serializedItem.item) {
      const { item } = serializedItem;

      filesToImport.push({
        driveID: serializedItem.driveId,
        id: item.id,
        name: item.name,
        key: item.key,
      });
    }

    return filesToImport;
  }, [] as ImportFile[]);

  const { importDrawingsFromDms: dmsFiles } = await new GQLRequest()
    .query(
      `
      mutation($projectId: String!, $providerType: DmsProviderKind!, $connectionId: String!, $imports: [DmsImportRequest], $folderDriveId: String, $folders: [String]){
        importDrawingsFromDms(projectid: $projectId, dmsType: $providerType, connectionid: $connectionId, imports: $imports, folderdriveid: $folderDriveId, folders: $folders)
      }
      `
    )
    .variables({
      projectId,
      providerType: gqlProviderType,
      connectionId,
      imports,
      folderDriveId,
      folders,
    })
    .json<{
      importDrawingsFromDms: string;
    }>();

  return dmsFiles;
};

export const disconnectAccount = async (
  connectionId: string,
  projectId: string
): Promise<string> => {
  const { disconnectDmsAccountFromProject } = await new GQLRequest()
    .query(
      `
    mutation($connectionId : String!, $projectId : String!){
      disconnectDmsAccountFromProject(connectionid: $connectionId, projectid: $projectId)
    }`
    )
    .variables({ connectionId, projectId })
    .json<{
      disconnectDmsAccountFromProject: string;
    }>();

  return disconnectDmsAccountFromProject;
};

export const exportProjectDataToIntegrations = async (
  driveId: string,
  connectionId: string,
  folderId: string | undefined,
  bbProjectId: string,
  providerType: string,
  filesToExport: {
    drawings: boolean;
    punch: boolean;
    rfis: boolean;
    submittals: boolean;
    users: boolean;
  }
): Promise<{
  exportAllDataToDms: {
    fileid: string;
    driveid: string;
    providertype: string;
    folderid: string;
  };
}> => {
  const input = {
    request: {
      projectid: bbProjectId,
      drawings: filesToExport.drawings,
      punch: filesToExport.punch,
      rfis: filesToExport.rfis,
      submittals: filesToExport.submittals,
      userlog: filesToExport.users,
    },
    connectionid: connectionId,
    providertype: providerType,
    driveid: driveId,
    folderid: folderId,
  };

  const {
    exportAllDataToDms: { fileid, driveid, providertype, folderid },
  } = await new GQLRequest()
    .query(
      `
      query($input: DmsExportAllRequest!) {
        exportAllDataToDms(input: $input){
          fileid
          driveid
          providertype
          folderid
        }
      }
    `
    )
    .variables({
      input,
    })
    .json<{
      exportAllDataToDms: {
        fileid: string;
        driveid: string;
        providertype: string;
        folderid: string;
      };
    }>();

  return {
    exportAllDataToDms: {
      driveid,
      fileid,
      folderid,
      providertype,
    },
  };
};

export const createNewFolder = async (
  folderName: string,
  projectId: string,
  connectionId: string,
  providerType: string,
  driveId: string,
  parent: string
): Promise<{ createDmsFolder: string }> => {
  const { createDmsFolder } = await new GQLRequest()
    .query(
      `
      mutation($projectid: String!,$connectionid: String!, $providertype: String!, $driveid: String!, $foldername: String!, $parent: String!  ){
        createDmsFolder(connectionid: $connectionid,  projectid: $projectid, providertype:$providertype, driveid: $driveid, foldername: $foldername, parent: $parent)
      }
      `
    )
    .variables({
      driveid: driveId,
      projectid: projectId,
      connectionid: connectionId,
      providertype: providerType,
      parent,
      foldername: folderName,
    })
    .json<{
      createDmsFolder: string;
    }>();

  return { createDmsFolder };
};
